import { Currency } from "../../../js/common/CurrencyFormatter";
import productListLoaderService, { ProductListLoaderEvent } from "./browse/ProductListLoaderService";
import ProductListBrowseContentContainer, { FilterChangedEvent } from "./browse/ProductListBrowseContentContainer";
import productListSearchFormService, { ProductListSearchEvent } from "./filter/ProductListSearchFormService";
import ProductListPageTypeConfig from "./ProductListPageTypeConfig";
import ProductListFilter, { HashProductListFilterValues } from "./filter/ProductListFilter";
import { FilterValues } from "./filter/FilterValuesCollection";
import ProductListFilterContentContainer, { OnClearFilterValueEvent, OnClearSearchQueryEvent } from "./filter/ProductListFilterContentContainer";
import productListFiltersModalService from "./filter/ProductListFiltersModalService";
import applyFilterButtonService, { ApplyFilterEvent } from "./filter/ApplyFilterButtonService";
import { assert } from "@/common/assert";

export default class ProductListContentContainer {
    private readonly productListFilterContentContainer: ProductListFilterContentContainer;
    private readonly productListBrowseContentContainer: ProductListBrowseContentContainer;

    private readonly productListFilter: ProductListFilter;

    constructor(pageTypeConfig: ProductListPageTypeConfig) {
        this.productListFilter = new ProductListFilter(
            pageTypeConfig.orderBy,
            pageTypeConfig.browser,
            pageTypeConfig.containerType,
            // eslint-disable-next-line @typescript-eslint/no-misused-spread
            Array.isArray(pageTypeConfig.filter) ? { ...pageTypeConfig.filter } as FilterValues : pageTypeConfig.filter,
            pageTypeConfig.httpQueryParams,
        );

        this.productListFilterContentContainer = new ProductListFilterContentContainer(this.productListFilter);
        this.productListBrowseContentContainer = new ProductListBrowseContentContainer(this.productListFilter);
    }

    public init(pageTypeConfig: ProductListPageTypeConfig, currency: Currency): void {
        this.initFixedApplyFilterButton();
        this.initProductListSearchForm(pageTypeConfig);
        productListFiltersModalService.init(pageTypeConfig, currency, this.productListFilter, pageTypeConfig.filterData);

        productListLoaderService.addEventListener(ProductListLoaderEvent.Type, () => this.initContent(pageTypeConfig, currency));
        this.productListFilterContentContainer.addEventListener(OnClearSearchQueryEvent.Type, () => productListSearchFormService.clearSearchQuery());
        this.productListFilterContentContainer.addEventListener(OnClearFilterValueEvent.Type, () => this.loadByFilterWithOverlay());
        this.productListBrowseContentContainer.addEventListener(FilterChangedEvent.Type, () => this.loadByFilterWithOverlay());
        this.initContent(pageTypeConfig, currency);
        this.tryLoadContentByHash();
    }

    private loadByFilterWithOverlay(): void {
        this.loadByFilter();
    }

    private loadByFilter(): void {
        void productListLoaderService.loadByFilter(this.productListFilterContentContainer.getCurrentFilter());
    }

    private initFixedApplyFilterButton(): void {
        applyFilterButtonService.addEventListener(ApplyFilterEvent.Type, () => this.applySelectedFilters());
    }

    private initProductListSearchForm(pageTypeConfig: ProductListPageTypeConfig): void {
        productListSearchFormService.init(pageTypeConfig);
        productListSearchFormService.addEventListener(ProductListSearchEvent.Type, (event: Event): void => {
            assert(event instanceof ProductListSearchEvent);
            this.productListFilterContentContainer.setSearchQueryFilterValue(event.query);
            this.loadByFilterWithOverlay();
        });
    }

    private initContent(pageTypeConfig: ProductListPageTypeConfig, currency: Currency): void {
        this.productListFilterContentContainer.initContent(pageTypeConfig, currency);
        this.productListBrowseContentContainer.initContent();
    }

    private tryLoadContentByHash(): void {
        if (globalThis.location.hash === "") {
            return;
        }

        try {
            this.productListFilterContentContainer.loadContentByHash(JSON.parse(decodeURI(globalThis.location.hash.slice(1))) as HashProductListFilterValues);
            productListSearchFormService.setSearchQuery(this.productListFilterContentContainer.getCurrentFilter().getSearchQueryValue());
            this.loadByFilter();
        } catch {
            // Empty
        }
    }

    private applySelectedFilters(): void {
        this.productListFilter.setPage(1);
        this.productListFilter.setFilterValues(applyFilterButtonService.getFilterValues());
        this.loadByFilterWithOverlay();
    }
}
